/**
 * @file BillingClientsPage.js
 * @description This page displays a list of clients for billing with their respective details such as name, code, tier, agency, and status.
 */
import styles from './Billing.module.scss';
import { useState, useEffect, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Toast from '../ui/toasts/Toast';
import Spinner from '../ui/spinners/Spinner';
import {
    FilterModal,
    useFilter,
    renderFilterIcon,
} from '../helpers/FilterComponent';
import { NavLink } from 'react-router-dom';
import { fetchClients } from '../../services/clientService';
import { useAuthContext } from '../../store/AuthContext';
import { handleNavigateToClient } from '../../utils/utils';
import { ROUTE_BUILDERS } from '../../constants/routes';
import { getText } from '../../i18n';
const SortDirections = {
    ASCENDING: 'asc',
    DESCENDING: 'desc',
};

const BillingClientsPage = () => {
    const navigate = useNavigate();
    const [clients, setClients] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [sortConfig, setSortConfig] = useState({
        key: 'name',
        direction: SortDirections.ASCENDING,
    });
    const [activeModal, setActiveModal] = useState(null);
    const [selections, setSelections] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [baseFilteredData, setBaseFilteredData] = useState([]);
    const [filteredData, applyFilter, clearAllFilters] =
        useFilter(baseFilteredData);
    const { user } = useAuthContext();

    useEffect(() => {
        const searchFiltered = clients.filter(
            client =>
                client.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                client.code?.toLowerCase().includes(searchTerm.toLowerCase()),
        );
        setBaseFilteredData(searchFiltered);
    }, [searchTerm, clients]);

    const openModal = column => {
        setActiveModal(column);
    };

    const closeModal = () => {
        setActiveModal(null);
    };

    const handleApply = (column, selectedOptions) => {
        setSelections(prev => ({ ...prev, [column]: selectedOptions }));
        applyFilter(column, selectedOptions);
    };

    const clearFilters = () => {
        setSelections({});
        setSearchTerm('');
        clearAllFilters();
    };

    const getOptions = column => {
        const filtersWithoutCurrent = { ...selections };
        delete filtersWithoutCurrent[column];

        const filteredData = clients.filter(client =>
            Object.entries(filtersWithoutCurrent).every(
                ([key, values]) =>
                    values.length === 0 || values.includes(client[key]),
            ),
        );

        return Array.from(new Set(filteredData.map(item => item[column])))
            .filter(Boolean)
            .sort((a, b) => {
                if (typeof a === 'string' && typeof b === 'string') {
                    return a.localeCompare(b);
                }
                return a - b;
            });
    };

    useEffect(() => {
        loadClients();
    }, []);

    const loadClients = async () => {
        try {
            const fetchedClients = await fetchClients([]);
            setClients(fetchedClients);
            setBaseFilteredData(fetchedClients);
        } catch (error) {
            if (error.name === 'AbortError') {
                return null;
            }
            console.error('Failed to fetch clients', error);
            Toast.error(getText('msg_7318fe9ba00c'));
        } finally {
            setIsLoading(false);
        }
    };

    const getBillStatusClass = billStatus => {
        switch (billStatus) {
            case 'Current':
                return 'ing-badge-active';
            case 'Past Due':
                return 'ing-badge-error';
            default:
                return 'ing-badge-inactive';
        }
    };

    const sortedClients = useMemo(() => {
        return [...filteredData].sort((a, b) => {
            let aValue = a[sortConfig.key];
            let bValue = b[sortConfig.key];

            if (!aValue) aValue = '';
            if (!bValue) bValue = '';

            if (typeof aValue === 'string') aValue = aValue.toLowerCase();
            if (typeof bValue === 'string') bValue = bValue.toLowerCase();

            if (aValue < bValue) {
                return sortConfig.direction === SortDirections.ASCENDING
                    ? -1
                    : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === SortDirections.ASCENDING
                    ? 1
                    : -1;
            }
            return 0;
        });
    }, [filteredData, sortConfig]);

    const handleSearch = event => {
        setSearchTerm(event.target.value);
    };

    const handleSort = key => {
        setSortConfig(prevConfig => ({
            key,
            direction:
                prevConfig.key === key &&
                prevConfig.direction === SortDirections.ASCENDING
                    ? SortDirections.DESCENDING
                    : SortDirections.ASCENDING,
        }));
    };

    const handleSortName = () => handleSort('name');
    const handleSortClientCode = () => handleSort('code');
    const handleSortTier = () => handleSort('tier');
    const handleSortAgency = () => handleSort('agency');
    const handleSortBillStatus = () => handleSort('billStatus');
    const handleSortContact = () => handleSort('contact');

    const openClientSite = client => {
        handleNavigateToClient(
            client.id,
            client.code,
            client.name,
            false,
            true,
        );
    };

    return isLoading ? (
        <Spinner width={50} className={styles.loadingSpinner} />
    ) : (
        <div className="page-content">
            <div className="card">
                <div className="card-body px-0 py-4">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <div className="float-start d-flex align-items-center">
                            <div>
                                <p className="page-title card-title ms-4 me-5">
                                    {getText('msg_abaec4522bf6')}
                                </p>
                                <p
                                    className="ing-portals-subtitle mt-1"
                                    style={{ marginTop: '10px' }}
                                >
                                    {getText('msg_3369d73391db')}
                                </p>
                            </div>
                        </div>

                        <div className="float-end d-flex align-items-center">
                            <span
                                type="button"
                                onClick={clearFilters}
                                className="ing-text-buttons mx-3"
                            >
                                <i className="bi bi-arrow-counterclockwise me-2" />
                                {getText('msg_381c4bf1d409')}
                            </span>
                        </div>
                    </div>

                    <div className="page-title card-title ms-4 me-5 mb-5">
                        <div className={styles.searchWrapper}>
                            <input
                                type="text"
                                className={styles.searchInput}
                                placeholder={getText('msg_97b2f8e2ba2a')}
                                value={searchTerm}
                                onChange={handleSearch}
                            />
                            <span className={styles.searchIcon}>
                                <i className="bi bi-search"></i>
                            </span>
                        </div>
                    </div>
                    <div className="table-responsive-xxl w-100">
                        <table className="table table-hover align-middle">
                            <thead className="custom-table-header table-light">
                                <tr>
                                    <th className="ps-4">
                                        <span className="table-column-header-text">
                                            {getText('msg_709a23220f2c')}
                                        </span>
                                        <span
                                            onClick={handleSortName}
                                            style={{ cursor: 'pointer' }}
                                            className="mx-2"
                                        >
                                            <svg
                                                width={11}
                                                height={6}
                                                viewBox="0 0 11 6"
                                                fill="none"
                                            >
                                                <path
                                                    transform={
                                                        sortConfig.key ===
                                                            'name' &&
                                                        sortConfig.direction ===
                                                            SortDirections.DESCENDING
                                                            ? 'rotate(180 5.25 3)'
                                                            : ''
                                                    }
                                                    d="M1 0.75L5.25 5.25L9.5 0.75"
                                                    stroke="#2F2B43"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </span>
                                        <span
                                            onClick={e => {
                                                e.stopPropagation();
                                                openModal('name');
                                            }}
                                            className={styles.cursorPointer}
                                        >
                                            {renderFilterIcon(
                                                selections['name']?.length > 0,
                                            )}
                                        </span>
                                        {activeModal === 'name' && (
                                            <FilterModal
                                                column={activeModal}
                                                options={getOptions(
                                                    activeModal,
                                                )}
                                                selectedOptions={
                                                    selections[activeModal] ||
                                                    []
                                                }
                                                onApply={handleApply}
                                                onClose={closeModal}
                                            />
                                        )}
                                    </th>
                                    <th>
                                        <span className="table-column-header-text">
                                            {getText('msg_6346915c34e2')}
                                        </span>
                                        <span
                                            onClick={handleSortClientCode}
                                            style={{ cursor: 'pointer' }}
                                            className="px-2"
                                        >
                                            <svg
                                                width={11}
                                                height={6}
                                                viewBox="0 0 11 6"
                                                fill="none"
                                            >
                                                <path
                                                    transform={
                                                        sortConfig.key ===
                                                            'code' &&
                                                        sortConfig.direction ===
                                                            SortDirections.DESCENDING
                                                            ? 'rotate(180 5.25 3)'
                                                            : ''
                                                    }
                                                    d="M1 0.75L5.25 5.25L9.5 0.75"
                                                    stroke="#2F2B43"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </span>
                                        <span
                                            onClick={e => {
                                                e.stopPropagation();
                                                openModal('code');
                                            }}
                                            className={styles.cursorPointer}
                                        >
                                            {renderFilterIcon(
                                                selections['code']?.length > 0,
                                            )}
                                        </span>
                                        {activeModal === 'code' && (
                                            <FilterModal
                                                column={activeModal}
                                                options={getOptions(
                                                    activeModal,
                                                )}
                                                selectedOptions={
                                                    selections[activeModal] ||
                                                    []
                                                }
                                                onApply={handleApply}
                                                onClose={closeModal}
                                            />
                                        )}
                                    </th>
                                    <th>
                                        <span className="table-column-header-text">
                                            {getText('msg_5bd44ebe63cd')}
                                        </span>
                                        <span
                                            onClick={handleSortTier}
                                            style={{ cursor: 'pointer' }}
                                            className="px-2"
                                        >
                                            <svg
                                                width={11}
                                                height={6}
                                                viewBox="0 0 11 6"
                                                fill="none"
                                            >
                                                <path
                                                    transform={
                                                        sortConfig.key ===
                                                            'tier' &&
                                                        sortConfig.direction ===
                                                            SortDirections.DESCENDING
                                                            ? 'rotate(180 5.25 3)'
                                                            : ''
                                                    }
                                                    d="M1 0.75L5.25 5.25L9.5 0.75"
                                                    stroke="#2F2B43"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </span>
                                        <span
                                            onClick={e => {
                                                e.stopPropagation();
                                                openModal('tier');
                                            }}
                                            className={styles.cursorPointer}
                                        >
                                            {renderFilterIcon(
                                                selections['tier']?.length > 0,
                                            )}
                                        </span>
                                        {activeModal === 'tier' && (
                                            <FilterModal
                                                column={activeModal}
                                                options={getOptions(
                                                    activeModal,
                                                )}
                                                selectedOptions={
                                                    selections[activeModal] ||
                                                    []
                                                }
                                                onApply={handleApply}
                                                onClose={closeModal}
                                            />
                                        )}
                                    </th>
                                    <th>
                                        <span className="table-column-header-text">
                                            {getText('msg_5c47e26c6ad2')}
                                        </span>
                                        <span
                                            onClick={handleSortAgency}
                                            style={{ cursor: 'pointer' }}
                                            className="px-2"
                                        >
                                            <svg
                                                width={11}
                                                height={6}
                                                viewBox="0 0 11 6"
                                                fill="none"
                                            >
                                                <path
                                                    transform={
                                                        sortConfig.key ===
                                                            'agency' &&
                                                        sortConfig.direction ===
                                                            SortDirections.DESCENDING
                                                            ? 'rotate(180 5.25 3)'
                                                            : ''
                                                    }
                                                    d="M1 0.75L5.25 5.25L9.5 0.75"
                                                    stroke="#2F2B43"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </span>
                                        <span
                                            onClick={e => {
                                                e.stopPropagation();
                                                openModal('agency');
                                            }}
                                            className={styles.cursorPointer}
                                        >
                                            {renderFilterIcon(
                                                selections['agency']?.length >
                                                    0,
                                            )}
                                        </span>
                                        {activeModal === 'agency' && (
                                            <FilterModal
                                                column={activeModal}
                                                options={getOptions(
                                                    activeModal,
                                                )}
                                                selectedOptions={
                                                    selections[activeModal] ||
                                                    []
                                                }
                                                onApply={handleApply}
                                                onClose={closeModal}
                                            />
                                        )}
                                    </th>
                                    <th>
                                        <span className="table-column-header-text">
                                            {getText('msg_e1a6ef7cd812')}
                                        </span>
                                        <span
                                            onClick={handleSortBillStatus}
                                            style={{ cursor: 'pointer' }}
                                            className="px-2"
                                        >
                                            <svg
                                                width={11}
                                                height={6}
                                                viewBox="0 0 11 6"
                                                fill="none"
                                            >
                                                <path
                                                    transform={
                                                        sortConfig.key ===
                                                            'billStatus' &&
                                                        sortConfig.direction ===
                                                            SortDirections.DESCENDING
                                                            ? 'rotate(180 5.25 3)'
                                                            : ''
                                                    }
                                                    d="M1 0.75L5.25 5.25L9.5 0.75"
                                                    stroke="#2F2B43"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </span>
                                        <span
                                            onClick={e => {
                                                e.stopPropagation();
                                                openModal('billStatus');
                                            }}
                                            className={styles.cursorPointer}
                                        >
                                            {renderFilterIcon(
                                                selections['billStatus']
                                                    ?.length > 0,
                                            )}
                                        </span>
                                        {activeModal === 'billStatus' && (
                                            <FilterModal
                                                column={activeModal}
                                                options={getOptions(
                                                    activeModal,
                                                )}
                                                selectedOptions={
                                                    selections[activeModal] ||
                                                    []
                                                }
                                                onApply={handleApply}
                                                onClose={closeModal}
                                            />
                                        )}
                                    </th>
                                    <th>
                                        <span className="table-column-header-text">
                                            {getText('msg_2c9b6f4e7a31')}
                                        </span>
                                        <span
                                            onClick={handleSortContact}
                                            style={{ cursor: 'pointer' }}
                                            className="px-2"
                                        >
                                            <svg
                                                width={11}
                                                height={6}
                                                viewBox="0 0 11 6"
                                                fill="none"
                                            >
                                                <path
                                                    transform={
                                                        sortConfig.key ===
                                                            'contact' &&
                                                        sortConfig.direction ===
                                                            SortDirections.DESCENDING
                                                            ? 'rotate(180 5.25 3)'
                                                            : ''
                                                    }
                                                    d="M1 0.75L5.25 5.25L9.5 0.75"
                                                    stroke="#2F2B43"
                                                    strokeWidth="1.5"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                        </span>
                                        <span
                                            onClick={e => {
                                                e.stopPropagation();
                                                openModal('contact');
                                            }}
                                            className={styles.cursorPointer}
                                        >
                                            {renderFilterIcon(
                                                selections['contact']?.length >
                                                    0,
                                            )}
                                        </span>
                                        {activeModal === 'contact' && (
                                            <FilterModal
                                                column={activeModal}
                                                options={getOptions(
                                                    activeModal,
                                                )}
                                                selectedOptions={
                                                    selections[activeModal] ||
                                                    []
                                                }
                                                onApply={handleApply}
                                                onClose={closeModal}
                                            />
                                        )}
                                    </th>
                                    <th className="text-center">
                                        <span className="table-column-header-text">
                                            {getText('msg_5b6825d3c67a')}
                                        </span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedClients.map(client => (
                                    <tr key={client.id}>
                                        <td className="item-page-name ps-4">
                                            <NavLink
                                                to={ROUTE_BUILDERS.billingClientsView(
                                                    client.id,
                                                )}
                                                className="titleCellBilling"
                                            >
                                                {client.name}
                                            </NavLink>
                                        </td>
                                        <td className="subtitleCell">
                                            {client.code}
                                        </td>
                                        <td className="item-page-changed-by">
                                            {client.tier ||
                                                getText('msg_08d2e98e6754')}
                                        </td>
                                        <td className="subtitleCell">
                                            {client.agency}
                                        </td>
                                        <td className="item-page-changed-by">
                                            <button
                                                className={getBillStatusClass(
                                                    client.billStatus,
                                                )}
                                            >
                                                {client.billStatus}
                                            </button>
                                        </td>
                                        <td className="item-page-changed-by">
                                            {client.contact ||
                                                getText('msg_08d2e98e6754')}
                                        </td>
                                        <td className="text-center">
                                            <span
                                                type="button"
                                                role="button"
                                                tabIndex={0}
                                                title={getText(
                                                    'msg_5b6825d3c67a',
                                                )}
                                                className={styles.iconButton}
                                                onClick={() =>
                                                    openClientSite(client)
                                                }
                                                onKeyDown={event => {
                                                    if (
                                                        event.key === 'Enter' ||
                                                        event.key === ' '
                                                    ) {
                                                        openClientSite(client);
                                                    }
                                                }}
                                            >
                                                <i className="bi bi-arrow-up-right-circle-fill" />
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <p
                        className="text-center fs-5"
                        style={{
                            margin: '60px 0',
                            color: 'rgba(47, 43, 67, 0.6)',
                        }}
                    >
                        {sortedClients.length <= 0
                            ? getText('msg_a1b2c3d4e5f6')
                            : ''}
                    </p>
                </div>
            </div>
        </div>
    );
};

export default BillingClientsPage;
